import * as React from "react";
import { Loader2, PlusCircle } from "lucide-react";
import {
  Button,
  Input,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  ADJUSTMENT_KIND_LABELS,
  formatPaise,
  istToday,
  rupeesToPaise,
  useCreateAdjustment,
  type AdjustmentKind,
} from "@/lib/teamPay/api";
import { ErrorBanner, Field } from "@/components/team-pay/shared";
import { errorMessage } from "@/lib/teamPay/words";

/** Phase SC-22d — add an incentive, bonus, reimbursement or deduction. */

const KINDS: AdjustmentKind[] = ["incentive", "bonus", "reimbursement", "deduction"];

export function AdjustmentSheet({
  open,
  onOpenChange,
  payeeId,
  memberName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payeeId: string | null;
  memberName: string;
}) {
  const [kind, setKind] = React.useState<AdjustmentKind>("incentive");
  const [amount, setAmount] = React.useState("");
  const [effectiveOn, setEffectiveOn] = React.useState(istToday());
  const [note, setNote] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const create = useCreateAdjustment();

  React.useEffect(() => {
    if (open) {
      setKind("incentive");
      setAmount("");
      setEffectiveOn(istToday());
      setNote("");
      setError(null);
    }
  }, [open]);

  const amountPaise = rupeesToPaise(amount);
  const canSubmit = payeeId !== null && amountPaise !== null && !create.isPending;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || payeeId === null || amountPaise === null) return;
    setError(null);
    try {
      await create.mutateAsync({
        staff_payee_id: payeeId,
        kind,
        amount_paise: amountPaise,
        effective_on: effectiveOn,
        note: note.trim() || null,
      });
      toast.success(
        `${ADJUSTMENT_KIND_LABELS[kind]} of ${formatPaise(amountPaise)} added for ${memberName}.`,
      );
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err, "Couldn't save. Please retry."));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[440px]"
        data-testid="team-adjustment-sheet"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <PlusCircle className="size-5" />
            </span>
            <div className="flex-1">
              <SheetTitle className="tracking-tight">Add to {memberName}'s pay</SheetTitle>
              <SheetDescription className="mt-1 text-xs">
                Counted in the month of the date you pick.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {error ? <ErrorBanner message={error} /> : null}
            <div
              role="radiogroup"
              aria-label="Type"
              className="bg-muted/40 grid grid-cols-2 gap-1 rounded-lg p-1 sm:grid-cols-4"
            >
              {KINDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={kind === k}
                  onClick={() => setKind(k)}
                  className={cn(
                    "rounded-md px-2 py-1.5 text-xs font-medium transition-[background-color,color,transform] duration-150 ease-out active:scale-[0.97]",
                    kind === k
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {ADJUSTMENT_KIND_LABELS[k]}
                </button>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Amount (₹)" htmlFor="tpj-amount" required>
                <Input
                  id="tpj-amount"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </Field>
              <Field label="Date" htmlFor="tpj-date" required>
                <Input
                  id="tpj-date"
                  type="date"
                  value={effectiveOn}
                  onChange={(e) => setEffectiveOn(e.target.value)}
                />
              </Field>
            </div>
            <Field label="Reason" htmlFor="tpj-note">
              <Input
                id="tpj-note"
                value={note}
                maxLength={1000}
                onChange={(e) => setNote(e.target.value)}
                placeholder={kind === "deduction" ? "Broken equipment" : "Exam season extra hours"}
              />
            </Field>
          </div>
          <div className="border-border/60 bg-card/40 flex items-center justify-end gap-2 border-t px-6 py-4 backdrop-blur">
            <SheetClose asChild>
              <Button variant="ghost" type="button" disabled={create.isPending}>
                Cancel
              </Button>
            </SheetClose>
            <Button type="submit" disabled={!canSubmit}>
              {create.isPending ? (
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              ) : null}
              Add {ADJUSTMENT_KIND_LABELS[kind].toLowerCase()}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
